"use client";

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { GoogleIcon, SearchIcon } from "../icons";
import styles from "./SourcesBars.module.css";

/**
 * Schéma de principe : des rendez-vous par mois pour un petit cabinet qui part
 * de quatre par mois (un par semaine), tableau validé par Pierre le 18/09.
 * Mois 1, le bouche-à-oreille seul. Mois 2, la publicité Google arrive et le
 * total triple presque. Mois 3, le référencement apparaît en filet et ne bouge
 * plus jusqu'au mois 6 : ce sont les quatre mois où l'on travaille sans rien
 * voir venir. Il décolle ensuite et passe devant la publicité au mois 12. Une
 * source absente vaut 0 : sa barre n'existe pas tant qu'elle n'est pas lancée.
 */
const MONTHS = [
  { word: 4, ads: 0, seo: 0 },
  { word: 4, ads: 9, seo: 0 },
  { word: 4, ads: 10, seo: 1 },
  { word: 4, ads: 10, seo: 1 },
  { word: 5, ads: 11, seo: 1 },
  { word: 5, ads: 11, seo: 1 },
  { word: 5, ads: 11, seo: 3 },
  { word: 5, ads: 12, seo: 5 },
  { word: 6, ads: 12, seo: 7 },
  { word: 6, ads: 12, seo: 9 },
  { word: 6, ads: 12, seo: 11 },
  { word: 7, ads: 12, seo: 14 },
] as const;

/** Le mois où chaque source est lancée, pour poser la mention au bon endroit. */
const LAUNCH = { ads: 2, seo: 3 } as const;

const MAX = Math.max(...MONTHS.map((m) => m.ads + m.seo + m.word));
const LAST = MONTHS.length;

/** Ordre d'empilement, du bas vers le haut. */
const STACK = ["word", "seo", "ads"] as const;

/* LE DÉROULÉ ACCÉLÈRE (21/09, demande de Pierre : « montrer l'effet boule de
   neige », « les barres apparaissent avec un rythme plutôt lent et au fur et à
   mesure elles s'accélèrent »). Il était linéaire, un mois toutes les 300 ms.

   L'intervalle rétrécit donc de 460 ms entre les deux premiers mois à 100 ms
   entre les deux derniers, soit une accélération de 4,6×. Le total passe de
   3,3 s à ~2,8 s : on gagne en nervosité sans rien perdre à la lecture, parce
   que les mois lents sont ceux où il n'y a qu'une source à comprendre.

   La courbe est en puissance 1,5 sur le temps RESTANT, pas sur le temps écoulé :
   c'est ce qui donne des écarts qui se resserrent au lieu de s'espacer.
   Les premières barres se posent une à une, les dernières se chevauchent (leur
   transition de hauteur dure 320 ms), et ce chevauchement EST l'effet cherché :
   à la fin, ça monte plus vite qu'on ne peut les compter. */
const STEP_MAX_MS = 460;
const STEP_MIN_MS = 100;
const STEP_CURVE = 1.5;

/** Délai avant le mois suivant, depuis le mois affiché (1 → LAST - 1). */
function stepFor(month: number) {
  const remaining = 1 - (month - 1) / (LAST - 1);
  return STEP_MIN_MS + (STEP_MAX_MS - STEP_MIN_MS) * remaining ** STEP_CURVE;
}

export type SourcesBarsProps = {
  /** Nom de chaque source, dans la légende. */
  sources: { word: ReactNode; ads: ReactNode; seo: ReactNode };
  /** Les deux lancements, posés au-dessus de leur colonne. `null` les retire. */
  milestones?: { ads: ReactNode; seo: ReactNode } | null;
};

/** La flèche droite qui descend de la mention vers sa colonne. */
function NoteArrow() {
  return (
    <svg className={styles.noteArrow} viewBox="0 0 12 40" fill="none" preserveAspectRatio="none" aria-hidden="true">
      <path d="M6 1v32" />
      <path d="M1.5 27.5L6 34l4.5-6.5" />
    </svg>
  );
}

/**
 * Les trois sources de rendez-vous en barres empilées sur douze mois : l'axe
 * des mois, le total de rendez-vous de chaque mois, les deux lancements
 * annotés au-dessus de leur colonne, et la légende posée sous le graphique
 * (elle prenait la place en haut, là où les mentions ont besoin d'air). La
 * mention d'exemple, elle, est posée par `SourcesChart` au-dessus du bloc :
 * dans le panneau, les mentions de lancement lui passaient dessus.
 * À l'arrivée à l'écran, les douze mois se déroulent une fois, un par un, puis
 * les deux mentions de lancement se posent l'une après l'autre : les barres
 * d'abord, le sens ensuite.
 * Rien ne se déroule en mouvement réduit, et sans JavaScript l'année entière
 * reste affichée.
 * Écrit le 18/09/2026 en reprise de l'illustration générée validée par Pierre.
 */
export function SourcesBars({ sources, milestones }: SourcesBarsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [month, setMonth] = useState<number>(LAST);
  const [playing, setPlaying] = useState(false);
  const [done, setDone] = useState(false);
  /* Combien de mentions de lancement sont posées : les deux d'emblée sans
     JavaScript, aucune pendant le déroulé, puis l'une après l'autre. */
  const [notes, setNotes] = useState(2);


  /* Au montage on revient au mois 1 et on attend l'arrivée à l'écran. Le rendu
     serveur, lui, garde l'année complète : sans JavaScript, on voit tout. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;
    /* Le retour au mois 1 passe par une frame : on ne pose pas d'état
       directement dans l'effet, et la section est de toute façon hors écran. */
    const frame = requestAnimationFrame(() => {
      setMonth(1);
      setNotes(0);
    });
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPlaying(true);
            observer.disconnect();
          }
        }
      },
      /* Le déroulé part quand la moitié du graphique est à l'écran, pas dès que
         son bord apparaît : sinon l'année est jouée avant qu'on la regarde. */
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  /* Le déroulé, une seule fois, à l'arrivée à l'écran. Un setTimeout qui se
     replanifie et non un setInterval : c'est ce qui permet à l'intervalle de
     rétrécir d'un mois à l'autre. */
  useEffect(() => {
    if (!playing) return;
    let id = 0;
    const tick = (current: number) => {
      if (current >= LAST) {
        setPlaying(false);
        setDone(true);
        return;
      }
      id = window.setTimeout(() => {
        setMonth(current + 1);
        tick(current + 1);
      }, stepFor(current));
    };
    tick(1);
    return () => window.clearTimeout(id);
  }, [playing]);

  /* L'année finie, on nomme ce qui s'est passé : d'abord le lancement de la
     publicité, puis celui du référencement. Deux temps, pour qu'on relie
     chaque mention à sa colonne. */
  useEffect(() => {
    if (!done) return;
    const first = window.setTimeout(() => setNotes(1), 500);
    const second = window.setTimeout(() => setNotes(2), 1400);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(second);
    };
  }, [done]);

  return (
    <div className={styles.bars} ref={ref} data-notes={notes}>
      {milestones ? (
        <ul className={styles.notesStacked}>
          <li>
            <span className={styles.notesMonth}>Mois {LAUNCH.ads}</span>
            {milestones.ads}
          </li>
          <li>
            <span className={styles.notesMonth}>Mois {LAUNCH.seo}</span>
            {milestones.seo}
          </li>
        </ul>
      ) : null}

      <div className={styles.plot}>
        <ol className={styles.columns}>
          {MONTHS.map((data, index) => {
            const total = data.word + data.ads + data.seo;
            const launch = index + 1 === LAUNCH.ads ? "ads" : index + 1 === LAUNCH.seo ? "seo" : null;
            return (
              <li
                key={index}
                className={styles.column}
                data-shown={index + 1 <= month ? "true" : "false"}
                style={{ "--total": total, "--max": MAX } as CSSProperties}
              >
                <span className={styles.stack}>
                  {milestones && launch ? (
                    <span className={styles.note} data-note={launch}>
                      {launch === "ads" ? (
                        <GoogleIcon className={styles.noteIcon} />
                      ) : (
                        <SearchIcon className={styles.noteIcon} />
                      )}
                      <span className={styles.noteText}>
                        {launch === "ads" ? milestones.ads : milestones.seo}
                      </span>
                      <NoteArrow />
                    </span>
                  ) : null}

                  <span className={styles.total}>
                    {total}
                    <small className={styles.unit}> RDV</small>
                  </span>

                  {STACK.map((key) =>
                    data[key] ? (
                      <span
                        key={key}
                        className={styles.segment}
                        data-source={key}
                        style={{ "--part": data[key] } as CSSProperties}
                      />
                    ) : null,
                  )}
                </span>
                <span className={styles.month}>
                  {index === 0 ? <span className={styles.monthWord}>Mois&nbsp;</span> : null}
                  {index + 1}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <ul className={styles.legend}>
        {STACK.map((key) => (
          <li key={key} className={styles.legendItem}>
            <span className={styles.swatch} data-source={key} />
            {sources[key]}
          </li>
        ))}
      </ul>
    </div>
  );
}
